"use client"

import { PASSWORD_MIN_LENGTH } from "@papier/auth-contracts"
import { cn } from "@papier/ui/lib/utils"

const STRENGTH_SEGMENTS = [1, 2, 3, 4] as const

type FilledLevel = "weak" | "fair" | "good" | "strong"

interface PasswordStrengthMeterProps {
    /** The password as typed. Renders nothing while it is empty. */
    password: string
    className?: string
}

interface PasswordStrength {
    score: number
    level: FilledLevel | "empty"
}

const SEGMENT_COLORS: Record<FilledLevel, string> = {
    weak: "bg-destructive",
    fair: "bg-amber-500",
    good: "bg-sky-500",
    strong: "bg-emerald-500"
}

const LEVEL_LABELS: Record<FilledLevel, string> = {
    weak: "Slabă",
    fair: "Medie",
    good: "Bună",
    strong: "Puternică"
}

const SEQUENTIAL_PATTERNS = [
    "abcdefghijklmnopqrstuvwxyz",
    "0123456789",
    "qwertyuiop"
]

function containsSequentialPattern(password: string): boolean {
    const lowercased = password.toLowerCase()

    return SEQUENTIAL_PATTERNS.some((pattern) => {
        const reversed = pattern.split("").reverse().join("")
        const directions = [pattern, reversed]

        return directions.some((direction) => {
            for (let index = 0; index + 4 <= direction.length; index += 1) {
                if (lowercased.includes(direction.slice(index, index + 4))) {
                    return true
                }
            }
            return false
        })
    })
}

function evaluatePasswordStrength(password: string): PasswordStrength {
    if (!password) {
        return { score: 0, level: "empty" }
    }

    let score = 0

    if (password.length >= PASSWORD_MIN_LENGTH) score += 1
    if (password.length >= PASSWORD_MIN_LENGTH + 4) score += 1
    if (password.length >= 16) score += 1

    const classCount = [
        /[a-z]/.test(password),
        /[A-Z]/.test(password),
        /\d/.test(password),
        /[^a-zA-Z\d]/.test(password)
    ].filter(Boolean).length

    if (classCount >= 3) score += 1
    if (classCount === 4) score += 1
    if (new Set(password).size <= 2) score -= 2
    if (containsSequentialPattern(password)) score -= 1

    if (password.length < PASSWORD_MIN_LENGTH) {
        return { score: 1, level: "weak" }
    }

    const clampedScore = Math.min(4, Math.max(1, score))
    const level: FilledLevel =
        clampedScore >= 4
            ? "strong"
            : clampedScore === 3
              ? "good"
              : clampedScore === 2
                ? "fair"
                : "weak"

    return { score: clampedScore, level }
}

export function PasswordStrengthMeter({
    password,
    className
}: PasswordStrengthMeterProps) {
    const { score, level } = evaluatePasswordStrength(password)

    if (level === "empty") {
        return null
    }

    return (
        <div className={cn("flex flex-col gap-1.5", className)}>
            <div aria-hidden="true" className="flex gap-1">
                {STRENGTH_SEGMENTS.map((segment) => (
                    <span
                        key={segment}
                        className={cn(
                            "h-1 flex-1 rounded-full bg-muted transition-colors",
                            segment <= score && SEGMENT_COLORS[level]
                        )}
                    />
                ))}
            </div>

            <p aria-live="polite" className="text-xs text-muted-foreground">
                Puterea parolei:{" "}
                <span className="font-medium text-foreground">
                    {LEVEL_LABELS[level]}
                </span>
            </p>
        </div>
    )
}
